import React, { useEffect, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box,
  FormControlLabel, Grid, Switch, TextField, Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

import type { PersonaDraft } from '../../services/playbookPersonaApi';
import { Dropdown } from '../ui/Dropdown';

// The persona's dropdowns are medium: every field beside them is a medium TextField.
const MANNER_OPTIONS = [
  { value: 'neutral', label: 'Neutral' },
  { value: 'curious', label: 'Curious' },
  { value: 'frustrated', label: 'Frustrated' },
  { value: 'aggressive', label: 'Aggressive' },
];

const TALKATIVENESS_OPTIONS = [
  { value: 'terse', label: 'Terse, short answers' },
  { value: 'normal', label: 'Normal' },
  { value: 'chatty', label: 'Chatty, wanders' },
];

/** Mirrors the limits on TrainingTrack.personaConfig; the server clamps too. */
const LIST_LIMITS: Record<string, { max: number; chars: number }> = {
  painPoints: { max: 6, chars: 200 },
  focusAreas: { max: 6, chars: 120 },
  advancedTalkingPoints: { max: 10, chars: 200 },
  hardRules: { max: 8, chars: 300 },
};

interface ListFieldProps {
  label: string;
  helper: string;
  field: keyof typeof LIST_LIMITS;
  values: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}

/** One line per entry, edited as text. Simpler than chip-entry and it lets an
 *  admin paste a list straight in. */
const ListField: React.FC<ListFieldProps> = ({ label, helper, field, values, onChange, disabled }) => {
  const limit = LIST_LIMITS[field];
  return (
    <TextField
      fullWidth
      multiline
      minRows={2}
      maxRows={8}
      label={label}
      value={(values || []).join('\n')}
      onChange={(event) =>
        onChange(
          event.target.value
            .split('\n')
            .map((line) => line.slice(0, limit.chars))
            .slice(0, limit.max)
        )
      }
      disabled={disabled}
      helperText={`${helper} One per line, up to ${limit.max}.`}
    />
  );
};

const OPENING_LINE_HELPER =
  'A greeting only, such as "Hi, thanks for making the time today." The customer raises '
  + 'needs and concerns later, when the salesperson asks.';

interface PersonaReviewProps {
  persona: PersonaDraft;
  onChange: (persona: PersonaDraft) => void;
  disabled?: boolean;
  /** Why the server refused the opening line on save; shown on the field. */
  openingLineError?: string | null;
}

/**
 * The synthesised persona, every field editable.
 *
 * Only the fields an admin most often corrects are shown up front: who the
 * customer is, what they want, what hurts, where they push, and their manner.
 * Everything else the model filled in sits under "More details" — still
 * editable, just not in the way.
 */
export const PersonaReview: React.FC<PersonaReviewProps> = ({
  persona, onChange, disabled = false, openingLineError = null,
}) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  // The opening line sits under "More details": open it when a save is
  // refused because of that field, or the admin never sees why.
  useEffect(() => {
    if (openingLineError) setIsDetailsOpen(true);
  }, [openingLineError]);

  const set = (patch: Partial<PersonaDraft>) => onChange({ ...persona, ...patch });
  const setDecision = (patch: Partial<NonNullable<PersonaDraft['decisionContext']>>) =>
    set({
      decisionContext: {
        budgetRange: '', hasAuthority: false, timeline: '',
        otherStakeholders: [], alternativesBeingEvaluated: [],
        ...(persona.decisionContext || {}),
        ...patch,
      },
    });

  const decision = persona.decisionContext;

  return (
    <Box>
      {persona.interpretationNotes && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Filled in for you:</strong> {persona.interpretationNotes}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Your description did not cover these, so they were invented to make the customer
            concrete. Change anything that does not fit.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth required label="Customer role" value={persona.aiCustomerRole || ''}
            onChange={(e) => set({ aiCustomerRole: e.target.value })} disabled={disabled}
            helperText="Name, job title and company, in one line."
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth multiline minRows={2} required label="What they are trying to buy"
            value={persona.buyingObjective || ''}
            onChange={(e) => set({ buyingObjective: e.target.value })} disabled={disabled}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ListField
            label="What is bothering them" helper="Their real pain points."
            field="painPoints" values={persona.painPoints || []}
            onChange={(painPoints) => set({ painPoints })} disabled={disabled}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ListField
            label="What they press hardest on" helper="Where they will not let go."
            field="focusAreas" values={persona.focusAreas || []}
            onChange={(focusAreas) => set({ focusAreas })} disabled={disabled}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Dropdown
            label="Manner" size="medium" width="field" disabled={disabled}
            value={persona.personality || 'neutral'}
            onChange={(personality) => set({ personality: personality as PersonaDraft['personality'] })}
            options={MANNER_OPTIONS}
          />
        </Grid>
      </Grid>

      <Accordion
        expanded={isDetailsOpen}
        onChange={(_event, isExpanded) => setIsDetailsOpen(isExpanded)}
        disableGutters
        elevation={0}
        sx={{ mt: 3, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography variant="subtitle2">More details</Typography>
            <Typography variant="caption" color="text.secondary">
              Organisation, what they use today, the decision, closing and the opening.
              Already filled in from your description. Open only to change them.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Who they are</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth multiline minRows={2} label="Their organisation"
            value={persona.accountProfile || ''}
            onChange={(e) => set({ accountProfile: e.target.value })} disabled={disabled}
            helperText="Size, sector, geography, how they buy."
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Dropdown
            label="How much they talk" size="medium" width="field" disabled={disabled}
            value={persona.talkativeness || 'normal'}
            onChange={(talkativeness) => set({ talkativeness: talkativeness as PersonaDraft['talkativeness'] })}
            options={TALKATIVENESS_OPTIONS}
          />
        </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Why they took the call</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            The customer knows all of this and reveals it when asked, never volunteers it.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth multiline minRows={2} label="What they use today"
            value={persona.currentSituation || ''}
            onChange={(e) => set({ currentSituation: e.target.value })} disabled={disabled}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth multiline minRows={2} label="What made them look now"
            value={persona.triggerEvent || ''}
            onChange={(e) => set({ triggerEvent: e.target.value })} disabled={disabled}
          />
        </Grid>
          </Grid>

      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>How the decision gets made</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth label="Budget" value={decision?.budgetRange || ''}
            onChange={(e) => setDecision({ budgetRange: e.target.value })} disabled={disabled}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth label="Timeline" value={decision?.timeline || ''}
            onChange={(e) => setDecision({ timeline: e.target.value })} disabled={disabled}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControlLabel
            control={(
              <Switch
                checked={Boolean(decision?.hasAuthority)} disabled={disabled}
                onChange={(e) => setDecision({ hasAuthority: e.target.checked })}
              />
            )}
            label="Can sign off alone"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth multiline minRows={2} label="Who else is involved"
            value={(decision?.otherStakeholders || []).join('\n')}
            onChange={(e) => setDecision({ otherStakeholders: e.target.value.split('\n').slice(0, 8) })}
            disabled={disabled} helperText="One per line."
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth multiline minRows={2} label="Who else they are looking at"
            value={(decision?.alternativesBeingEvaluated || []).join('\n')}
            onChange={(e) => setDecision({ alternativesBeingEvaluated: e.target.value.split('\n').slice(0, 8) })}
            disabled={disabled} helperText="One per line."
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Closing</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        What counts as the salesperson genuinely pushing for a next step on <em>this</em> sale.
        Scoring uses these, merged with the matching sales playbook.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ListField
            label="Advanced talking points" helper="What a strong close looks like here."
            field="advancedTalkingPoints" values={persona.advancedTalkingPoints || []}
            onChange={(advancedTalkingPoints) => set({ advancedTalkingPoints })} disabled={disabled}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth multiline minRows={2} label="What ends the call"
            value={persona.endConditions || ''}
            onChange={(e) => set({ endConditions: e.target.value })} disabled={disabled}
            helperText="What earns a next step, and what makes them disengage."
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>How the call opens</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* The customer always opens (as in knowledge base mode), so there is no
            "who speaks first" choice: the rep app waits for the customer's voice. */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth label="Their opening line (optional)" value={persona.openingLine || ''}
            onChange={(e) => set({ openingLine: e.target.value })}
            disabled={disabled}
            error={Boolean(openingLineError)}
            helperText={openingLineError || OPENING_LINE_HELPER}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <ListField
            label="Things they must never do" helper="Hard rules for the character."
            field="hardRules" values={persona.hardRules || []}
            onChange={(hardRules) => set({ hardRules })} disabled={disabled}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth multiline minRows={2} label="Anything else (optional)"
            value={persona.additionalInstructions || ''}
            onChange={(e) => set({ additionalInstructions: e.target.value })} disabled={disabled}
            helperText="Added to the customer's instructions word for word."
          />
        </Grid>
      </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PersonaReview;
